export interface LapMarkers {
  edited?: boolean | null;
  idx_end?: number[] | null;
  idx_start?: number[] | null;
  inactive_laps: boolean[] | null;
  markers_rel_ts?: number[] | null;
  start_ts?: number | null;
}

// Health status enum values (mirrors backend RunHealth IntEnum)
export enum RunHealthStatus {
  GOOD = 0,
  NONE = 1,
  AVERAGE = 50,
  WARNING = 100,
  ERROR = 500,
}

export enum ProcessingStage {
  INGESTION = 1500,
  METADATA = 2000,
  SENSORS = 3000,
  SESSION_SPECIFIC_PRE_GRID = 4000,
  ROUTE_PRE_GRID = 5000,
  HEALTH_CHECK = 5500,
  GRID = 6000,
  ROUTE_POST_GRID = 6500,
  SESSION_SPECIFIC_POST_GRID = 7000,
  CDA_CALCULATION = 8000,
  POST_CDA_CALCULATION = 9000,
  FINALIZATION = 10000,
  COMPLETE = 100000,
}

export function getStageName(code: number | null | undefined): string {
  if (code == null) return 'Unknown';
  if (code >= 100000) return 'Complete';
  if (code >= 10000) return 'Finalization';
  if (code >= 9000) return 'Post-CdA';
  if (code >= 8000) return 'CdA Calculation';
  if (code >= 7000) return 'Post-Grid Processing';
  if (code >= 6500) return 'Route Post-Grid';
  if (code >= 6000) return 'Grid';
  if (code >= 5500) return 'Health Check';
  if (code >= 5000) return 'Route Pre-Grid';
  if (code >= 4000) return 'Pre-Grid Processing';
  if (code >= 3000) return 'Sensors';
  if (code >= 2000) return 'Metadata';
  if (code >= 1500) return 'Ingestion';
  return 'Not Started';
}

export interface AntSensorHealth {
  sensor_key: string;
  present: boolean;
  time: number | null;
  packet_count: number | null;
  packet_count_sequential: boolean;
  packets_dropped: number;
  packets_dropped_fraction: number | null;
  packets_collision: number;
}

export interface GpsHealth {
  present: boolean;
  accuracy_p90: number | null;
  packet_count: number | null;
  time_seconds: number | null;
  pct_under_4m: number | null;
  pct_4_to_8m: number | null;
  pct_8_to_20m: number | null;
  pct_over_20m: number | null;
}

export interface GateHealth {
  present: boolean;
  gate_a_lat: number | null;
  gate_a_lon: number | null;
  gate_a_beta: number | null;
  gate_b_lat: number | null;
  gate_b_lon: number | null;
  gate_b_beta: number | null;
  gates_lat: number[] | null;
  gates_lon: number[] | null;
  bearing_deg: number | null;
  route_and_gates_intersect?: boolean | null;
  gates_defined_count?: number;
  gates_crossed_count?: number;
}

export interface RouteHealth {
  present: boolean;
  start_lat: number | null;
  start_lon: number | null;
  end_lat: number | null;
  end_lon: number | null;
  route_length_m: number | null;
  segment_count: number | null;
  bearing_deg: number | null;
}

export interface GateIntersection {
  route_and_gates_intersect: boolean | null;
  gates_defined_count: number;
  gates_crossed_count: number;
  fallback_level?: number;
}

// --- Solve object types ---

export type GateStatus = 'UNUSED' | 'START' | 'SPLIT' | 'FINISH';

export interface SolveGate {
  lat: number;
  lon: number;
  pos?: number | null;
  bearing?: number | null;
  status: GateStatus;
}

export interface SolveGateIntersection {
  gate_index: number;
  start_ts: number;
  duration?: number | null;
  status: GateStatus;
}

export interface SolveRouteMetadata {
  route_origin: string;
  route_origin_init: string;
  gates_origin: string;
  gates_origin_init: string;
  route_valid: boolean;
  gates_valid: boolean;
  incorrect_db_data: boolean;
  route_type: string;
  route_category: number;
  fallback_used: boolean;
  fallback_detail?: string | null;
}

export interface SolveData {
  version: number;
  cp_corr?: number | null;
  yaw_offset?: number | null;
  wheel_circumference?: number | null;
  gates?: SolveGate[] | null;
  route_metadata?: SolveRouteMetadata | null;
  gate_intersections?: SolveGateIntersection[] | null;
  gate_hash?: number | null;
  input_wheel_circumference?: number | null;
}

export interface BodySensorHealth {
  sensor_key: string;
  present: boolean;
  time: number | null;
  packet_count: number | null;
  packet_count_sequential: boolean;
  packets_dropped: number;
  packets_dropped_fraction: number | null;
  packets_collision: number;
  ref_chest_cm: number | null;
  ref_head_cm: number | null;
  ref_waist_cm: number | null;
}

export interface WindDiffRefs {
  diff_ref_1_pa: number | null;
  diff_ref_2_pa: number | null;
  diff_ref_3_pa: number | null;
}


export interface SensorInfoObj {
  ant_device_number: number | null;
  ant_id_not_found_reason: string | null;
  battery_voltage: number | null;
  firmware_version: string | null;
}

export interface SpeedHealthObj {
  wheel_skip_count: number | null;
  wheel_skip_ratio: number | null;
  gps_wheel_crc_in_tolerance: number;
  tolerance: number;
}

export enum DiffSensorStatus {
  GOOD = 0,
  NEEDS_AUTOCAL = 1,
  ERROR = 2,
  UNSTABLE = 3,
  UNKNOWN = 4,
}

export interface DiffChannelHealth {
  start_median_pa: number | null;
  start_std_pa: number | null;
  end_median_pa: number | null;
  end_std_pa: number | null;
  reference_pa: number | null;
  status: number;
}

export interface ProbeHealth {
  mean_abs_dev: number | null;
  n_samples: number | null;
  status: number;
}

export interface WindSensorHealth {
  diff1: DiffChannelHealth;
  diff2: DiffChannelHealth;
  diff3: DiffChannelHealth;
  probe_health?: ProbeHealth;
}

// ── Lap parsing health diagnostics ──

export interface CatesbyLapDetection {
  lap_number: number;
  threshold_crossing_idx: number;
  stability_confirmed_idx: number;
  lap_start_idx: number;
  lap_finish_idx: number;
  threshold_crossing_time_s: number;
  stability_confirmed_time_s: number;
  lap_start_time_s: number;
  lap_finish_time_s: number;
  lap_duration_s: number;
  threshold_crossing_distance_m: number;
  lap_start_distance_m: number;
  lap_finish_distance_m: number;
  deceleration_alert: boolean;
  active: boolean;
}

export interface CatesbyRejectedCrossing {
  crossing_idx: number;
  crossing_time_s: number;
  crossing_distance_m: number;
  target_start_idx?: number | null;
  target_start_distance_m?: number | null;
  reason: string;
}

export interface LapParsingHealth {
  parser_type: string;
  laps_detected: number;
  laps_active: number;
  // Catesby-specific fields (present when parser_type === 'catesby')
  speed_threshold_revs?: number;
  stability_window_s?: number;
  distance_offset_m?: number;
  lap_length_m?: number;
  wheel_circumference_m?: number;
  data_interval_s?: number;
  speed_80th_percentile_revs?: number | null;
  total_crossings_found?: number;
  total_distance_m?: number | null;
  total_duration_s?: number | null;
  deceleration_warning?: boolean;
  laps_with_deceleration?: number;
  laps?: CatesbyLapDetection[];
  rejected_crossings?: CatesbyRejectedCrossing[];
}

export interface RunHealthObj {
  sensors_present: {
    power_meter: AntSensorHealth;
    wind: AntSensorHealth;
    aero?: AntSensorHealth; // legacy key, use wind instead
    speed: AntSensorHealth;
    body?: BodySensorHealth;
    power_meter_2?: AntSensorHealth;
    power_meter_3?: AntSensorHealth;
    speed_2?: AntSensorHealth;
    speed_3?: AntSensorHealth;
    gps: GpsHealth;
    gates: GateHealth;
    gates_run_original?: GateHealth;
    route: RouteHealth;
  };
  missing_sensors: string[];
  missing_optional_sensors?: string[];
  wind_diff_refs?: WindDiffRefs;
  sensor_info?: Record<string, SensorInfoObj>;
  gate_intersection?: GateIntersection;
  speed_health: SpeedHealthObj;
  wind_sensor_health?: WindSensorHealth;
  health_status: number;
  health_status_name: string;
  health_status_reasons?: string[];
  health_code?: number;
  total_run_time_seconds?: number;
  lap_parsing?: LapParsingHealth | null;
}

// ── Parser variable overrides ──

export interface CatesbyParserVars {
  speed_threshold?: number | null;
  distance_offset?: number | null;
  stability_window?: number | null;
  acceleration_smoothing?: number | null;
  lap_length?: number | null;
  min_duration?: number | null;
}

export interface TraverseParserVars {
  min_valid_points?: number | null;
  max_radius?: number | null;
  combine_gaps?: number | null;
  min_distance?: number | null;
}

export interface ParserVars {
  version: number;
  catesby?: CatesbyParserVars | null;
  velodrome?: Record<string, unknown> | null;
  traverse?: TraverseParserVars | null;
}

// ── Sensor offset overrides ──

export interface WindSensor5ZeroOverride {
  diff1_zero_pa?: number | null;
  diff2_zero_pa?: number | null;
  diff3_zero_pa?: number | null;
  override_ts?: number | null;
  source?: string | null;
}

export interface SensorOffsets {
  version: number;
  wind_sensor_5?: WindSensor5ZeroOverride | null;
}

export type SensorOffsetsScope = 'this' | 'after' | 'before' | 'all';

export interface Run {
  id: string;
  run_ts: string;
  run_status: number;
  description: string;
  hidden: boolean;
  deleted: boolean;
  run_number: number;
  run_ts_tz: string;
  average: Record<string, number[]>;
  deltas: Record<string, number[]>;
  traverse: Record<string, number[]>;
  yaw_offset?: number;
  solve?: SolveData | null;
  parser_vars?: ParserVars | null;
  sensor_offsets?: SensorOffsets | null;
  lap_markers?: LapMarkers | null;
  run_health?: number | null;
  run_health_obj?: RunHealthObj | null;
  health_code?: number | null;
}
